import React, { createContext, useContext } from 'react';
import { QueryObserverResult, RefetchOptions, useQuery } from '@tanstack/react-query';
import { makeLogger } from '@/utils/logger/logger.ts';
import { readHousesHousesGetOptions } from '@/client/@tanstack/react-query.gen.ts';
import { House } from '@/client';
import { ClassifiedHouse, classifyHouses } from '@/utils/properties/propertyGroupingUtils';
import { GlobalOverlayLoader } from '@/components/common/GlobalOverlayLoader.tsx';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { houseSelectors } from '@/utils/houseUtils.ts';
import { useAuth } from '@/context/AuthContext';
import { Routes } from '@/routes';
import { safeGetItem } from '@/utils/storage/storageUtils.ts';
import { SELECTED_HOUSE_ID_KEY, SELECTED_HOUSE_PUBLIC_ID_KEY } from '@/utils/storage/constants.ts';
import { setSentryHouseContext } from '@/utils/sentry/sentryReporter';

const logger = makeLogger('HouseContext');

export interface HouseContextType {
	houses: ClassifiedHouse[];
	completeHouses: ClassifiedHouse[];
	draftHouses: ClassifiedHouse[];
	selectedHouse: ClassifiedHouse | null;
	selectedHouseId: number | undefined;
	setSelectedHouseId: (houseId: number | undefined) => void;
	resetSelectedHouse: () => void;
	isLoading: boolean;
	isSelecting: boolean;
	isError: boolean;
	error: Error | null;
	refetch: (options?: RefetchOptions | undefined) => Promise<QueryObserverResult<ClassifiedHouse[], Error>>;
}

const HouseContext = createContext<HouseContextType | undefined>(undefined);

/**
 * Query options for the /houses list.
 *
 * The non-superadmin branch must pass NO `query` object, not an empty one: the generated
 * key builder does `if (options?.query) params.query = options.query`, and `{}` is truthy.
 * Passing `query: {}` therefore produces a different key from the `_authenticated` route
 * loader's `readHousesHousesGetOptions()` prefetch, so the prefetched result is never
 * reused and this provider refetches /houses behind a full-screen blocking loader on every
 * cold load. The request itself is identical either way — only the cache key differs.
 *
 * Covered by HouseContext.queryKey.test.tsx.
 */
export const houseListQueryOptions = (isSuperAdmin: boolean) =>
	readHousesHousesGetOptions(isSuperAdmin ? { query: { limit: 250 } } : {});

export const HouseProvider: React.FC<{ children: React.ReactNode; currentPath: string }> = (props) => {
	const { currentPath, children } = props;

	// Persist and control selection by ID
	const [selectedHouseId, setSelectedHouseId] = useLocalStorage<number | undefined>(SELECTED_HOUSE_ID_KEY, undefined);

	// Get user data and loading state
	const { isUserLoading, canFetchOtherData, isSuperAdmin } = useAuth();

	// Annotate houses with `isCopy` once, here at the data boundary, so presentational components read
	// the flag instead of re-deriving it.
	const classify = React.useCallback(
		(data: House[] | undefined): ClassifiedHouse[] => classifyHouses(data ?? []),
		[],
	);

	const {
		data: houses = [],
		isLoading: queryIsLoading,
		isError,
		error,
		refetch,
	} = useQuery({
		...houseListQueryOptions(isSuperAdmin),
		staleTime: 2 * 60 * 1000,
		select: classify,
		enabled: canFetchOtherData, // Only run query when user data is loaded
	});

	const selectedHouse = selectedHouseId ? houseSelectors.findById(houses, selectedHouseId) : null;
	const isLoading = queryIsLoading || isUserLoading;
	// Selection is pending when data is loaded but we haven't resolved/validated a selection yet
	const isSelecting = !isLoading && houses.length > 0 && (!selectedHouseId || !selectedHouse);

	// Selection management respecting onboarding and dashboard navigation
	React.useEffect(() => {
		if (houses.length === 0) return;

		// On /setup/new: always clear selection so the flow starts with a blank house
		if (currentPath === Routes.HOUSE_SETUP_NEW) {
			if (selectedHouseId) {
				logger.info(
					`[${currentPath}] Clearing selectedHouseId ${selectedHouseId} — starting fresh for new property`,
				);
				setSelectedHouseId(undefined);
			}
			return;
		}

		// If we already have a valid selection, keep it
		if (selectedHouseId && houses.some((h) => h.id === selectedHouseId)) {
			logger.info(`[${currentPath}] Keeping existing selection: house ${selectedHouseId}`);
			return;
		}

		// Derive draft/complete state (inline to avoid ordering issues)
		const complete = houseSelectors.complete(houses);
		const drafts = houseSelectors.drafts(houses);
		const anyComplete = complete.length > 0;
		const firstDraft = drafts[0];

		// On /setup: resume draft if there's no completed house yet
		if (currentPath === Routes.HOUSE_SETUP) {
			if (!anyComplete && firstDraft) {
				logger.info(`[${currentPath}] Resuming draft house ${firstDraft.id}`);
				setSelectedHouseId(firstDraft.id);
				return;
			}
			// Otherwise, do not auto-select while on onboarding
			logger.info(
				`[${currentPath}] On setup, not auto-selecting (anyComplete=${anyComplete}, hasDraft=${!!firstDraft})`,
			);
			return;
		}

		// Off onboarding: try last selected from localStorage
		const last = Number(safeGetItem(SELECTED_HOUSE_ID_KEY) || '');
		if (last && houses.some((h) => h.id === last)) {
			logger.info(`[${currentPath}] Restoring last selected house ${last} from localStorage`);
			setSelectedHouseId(last);
			return;
		}

		// Fallback: select the first available house
		logger.info(`[${currentPath}] Fallback: selecting first house ${houses[0].id}`);
		setSelectedHouseId(houses[0].id);
	}, [houses, selectedHouseId, currentPath, setSelectedHouseId]);

	// Update Sentry context when selected house changes
	React.useEffect(() => {
		setSentryHouseContext(selectedHouse);
	}, [selectedHouse]);

	// Persist public_id to localStorage so old-URL redirects can resolve the correct property
	React.useEffect(() => {
		if (selectedHouse?.public_id) {
			localStorage.setItem(SELECTED_HOUSE_PUBLIC_ID_KEY, selectedHouse.public_id);
		}
	}, [selectedHouse]);

	const resetSelectedHouse = () => {
		setSelectedHouseId(undefined);
	};

	const completeHouses = houseSelectors.complete(houses);
	const draftHouses = houseSelectors.drafts(houses);

	const contextValue: HouseContextType = {
		houses,
		completeHouses,
		draftHouses,
		selectedHouse,
		selectedHouseId,
		setSelectedHouseId,
		resetSelectedHouse,
		isLoading,
		isSelecting,
		isError,
		error: error as Error | null,
		refetch,
	};

	if (isLoading) {
		return <GlobalOverlayLoader text="Loading your properties ..." />;
	}

	return <HouseContext.Provider value={contextValue}>{children}</HouseContext.Provider>;
};

export const useHouseContext = () => {
	const context = useContext(HouseContext);
	if (context === undefined) {
		throw new Error('useHouseContext must be used within a HouseProvider');
	}
	return context;
};
